import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import {
  PathPickMutationProduct,
  PathPickProduct,
  PathPickProductKey,
  PathPickProductRequestKind,
} from "@/features/pathpick/types";
import {
  isPathPickProductSoldOut,
  toPathPickProductKey,
} from "@/features/pathpick/utils";
import { getRequestableProducts } from "@/features/pathpick/features/basket/utils";
import {
  PathPickBasketFormValues,
  PathPickBasketSelection,
} from "@/features/pathpick/features/basket/types";
import { PathPickProductIdsResponse } from "@/features/pathpick/types/response";

type SelectionMap = Map<PathPickProductKey, PathPickBasketSelection>;

const toItemKey = (item: Pick<PathPickProduct, "id" | "productType">) =>
  toPathPickProductKey(item.productType, item.id);

// KREAM 정보(kream)가 있으면 KREAM 매칭 상품 — KREAM 파일 대상 집계에 쓴다.
// 요청 슬롯 3종(requests)은 재신청 가능 판정에 쓴다 — 선택은 페이지를 넘겨도 유지되므로,
// 나중에 현재 페이지 목록에서 되찾을 수 없어 선택 시점에 함께 저장해야 한다.
const toSelection = (item: PathPickProduct): PathPickBasketSelection => ({
  id: item.id,
  type: item.productType,
  isKreamMatched: Boolean(item.kream),
  isSoldOut: isPathPickProductSoldOut(item),
  requests: item.requests ?? null,
});

// 검색 결과 전체 선택(ids_only) 항목 → 선택 값. 응답에 id·유형·판매 가능 여부만 와서
// KREAM 매칭·신청 이력은 알 수 없다 — 미매칭·미신청으로 둔다(중복 신청은 서버가 막는다).
const toResultSelection = (
  result: PathPickProductIdsResponse["products"][number]
): PathPickBasketSelection => ({
  id: result.id,
  type: result.productType,
  isKreamMatched: false,
  isSoldOut: isPathPickProductSoldOut(result),
  requests: null,
});

// 담은 상품 페이지의 교차 클라이언트 상태(선택/주문 모달 payload)를 Formik 컨텍스트로 공유한다.
// 이 훅은 <FormikProvider> 하위에서만 사용해야 한다.
//
// 반환하는 함수들은 selectedProducts를 클로저로 잡지 않고 setFieldValue의 함수형 업데이터를 쓴다.
// setFieldValue는 formik이 useEventCallback으로 만들어 identity가 고정이므로, 이 함수들도 렌더 간
// 고정된다 — 카드(React.memo) 경계가 유지되려면 이게 전제조건이다.
const usePathPickBasketSelection = () => {
  const { values, setFieldValue } =
    useFormikContext<PathPickBasketFormValues>();
  const { selectedProducts, orderModalItems } = values;

  const toggleItem = useCallback(
    (item: PathPickProduct) => {
      setFieldValue("selectedProducts", (prev: SelectionMap) => {
        const next = new Map(prev);
        const key = toItemKey(item);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, toSelection(item));
        }
        return next;
      });
    },
    [setFieldValue]
  );

  // 전체 선택은 "현재 페이지 목록" 기준으로 토글한다(선택 자체는 페이지 이동에도 유지).
  // 전부 선택돼 있을 때만 해제하고, 비었거나 일부만(indeterminate) 선택돼 있으면 전부 선택한다
  // — 검색 결과 전체 선택과 같은 규칙.
  const toggleAllOnPage = useCallback(
    (items: PathPickProduct[]) => {
      setFieldValue("selectedProducts", (prev: SelectionMap) => {
        const next = new Map(prev);
        const isAllSelected =
          items.length > 0 && items.every((item) => next.has(toItemKey(item)));
        if (isAllSelected) {
          items.forEach((item) => next.delete(toItemKey(item)));
        } else {
          items.forEach((item) => next.set(toItemKey(item), toSelection(item)));
        }
        return next;
      });
    },
    [setFieldValue]
  );

  // 검색 결과 전체 선택 — 이미 선택된 상품은 덮어쓰지 않는다. 페이지에서 고른 항목은
  // KREAM 매칭·신청 이력까지 갖고 있어 id 응답으로 만든 값보다 정확하다.
  const selectResults = useCallback(
    (results: PathPickProductIdsResponse["products"]) => {
      setFieldValue("selectedProducts", (prev: SelectionMap) => {
        const next = new Map(prev);
        results.forEach((result) => {
          const key = toItemKey(result);
          if (!next.has(key)) {
            next.set(key, toResultSelection(result));
          }
        });
        return next;
      });
    },
    [setFieldValue]
  );

  // 유형까지 받아야 다른 유형의 같은 숫자 id 선택을 건드리지 않는다.
  const removeProducts = useCallback(
    (products: PathPickMutationProduct[]) => {
      setFieldValue("selectedProducts", (prev: SelectionMap) => {
        const next = new Map(prev);
        products.forEach(({ id, type }) =>
          next.delete(toPathPickProductKey(type, id))
        );
        return next;
      });
    },
    [setFieldValue]
  );

  // 품절 제외 — 선택에서 품절 상품만 뺀다(품절이 섞이면 신청/주문이 막히므로 플로팅 바에서 쓴다).
  const removeSoldOut = useCallback(
    () =>
      setFieldValue("selectedProducts", (prev: SelectionMap) => {
        const next = new Map(prev);
        next.forEach(({ isSoldOut }, key) => {
          if (isSoldOut) {
            next.delete(key);
          }
        });
        return next;
      }),
    [setFieldValue]
  );

  const clear = useCallback(
    () => setFieldValue("selectedProducts", new Map()),
    [setFieldValue]
  );

  const openOrderModal = useCallback(
    (items: PathPickProduct[]) => setFieldValue("orderModalItems", items),
    [setFieldValue]
  );
  const closeOrderModal = useCallback(
    () => setFieldValue("orderModalItems", null),
    [setFieldValue]
  );

  const kreamMatchedCount = useMemo(
    () =>
      Array.from(selectedProducts.values()).filter(
        ({ isKreamMatched }) => isKreamMatched
      ).length,
    [selectedProducts]
  );

  // 품절 상품이 선택돼 있으면 등록 신청/주문을 막는다(안내 문구도 이 수치로 만든다).
  const soldOutCount = useMemo(
    () =>
      Array.from(selectedProducts.values()).filter(({ isSoldOut }) => isSoldOut)
        .length,
    [selectedProducts]
  );

  // 주어진 신청 종류들로 실제 접수될 상품 — 이미 완료됐거나 처리 중인 상품은 빠진다.
  // 파일 신청은 모달에서 양식 체크가 바뀔 때마다 대상이 달라져 값이 아니라 함수로 노출한다.
  const getRequestableProductsFor = useCallback(
    (kinds: PathPickProductRequestKind[]) =>
      getRequestableProducts(selectedProducts, kinds),
    [selectedProducts]
  );

  return {
    selectedProducts,
    selectedCount: selectedProducts.size,
    kreamMatchedCount,
    soldOutCount,
    getRequestableProductsFor,
    toggleItem,
    toggleAllOnPage,
    selectResults,
    removeProducts,
    removeSoldOut,
    clear,
    orderModalItems,
    openOrderModal,
    closeOrderModal,
  };
};

export default usePathPickBasketSelection;
